import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  BookOpen,
  Trophy,
  Flame,
  TrendingUp,
  Clock,
  CheckCircle,
  Circle,
  ArrowRight,
} from 'lucide-react';
import { progressService } from '../services/progressService';
import { courseService } from '../services/courseService';
import { useAuthStore } from '../store/useAuthStore';

const Dashboard = () => {
  const { user, isAuthenticated } = useAuthStore();
  const [stats, setStats] = useState(null);
  const [streak, setStreak] = useState(null);
  const [progress, setProgress] = useState(null);
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    if (isAuthenticated) {
      fetchDashboardData();
    } else {
      setIsLoading(false);
    }
  }, [isAuthenticated]);
  
  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);
      
      // Fetch all data in parallel
      const [statsData, streakData, progressData, coursesData] = await Promise.all([
        progressService.getStats(),
        progressService.getStreak(),
        progressService.getAllProgress(),
        courseService.getAllCourses(),
      ]);
      
      setStats(statsData);
      setStreak(streakData);
      setProgress(progressData);
      setCourses(coursesData.courses || coursesData);
    } catch (error) {
      toast.error('Failed to load dashboard data');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };
  
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center max-w-md">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Welcome to Python Learning!
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Log in to access your dashboard and track your learning progress.
          </p>
          <Link to="/login" className="btn-primary">
            Log In
          </Link>
        </div>
      </div>
    );
  }
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading your dashboard...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <div className="bg-black dark:bg-gray-950 text-white border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-4xl md:text-5xl font-black mb-3">
            Welcome back, <span className="text-highlight">{user?.display_name || 'Learner'}</span>!
          </h1>
          <p className="text-xl text-gray-400">
            Continue your Python learning journey
          </p>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Streak */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-800 hover:border-primary-500 transition-all duration-200 group">
            <div className="flex items-center justify-between mb-3">
              <div className="w-14 h-14 bg-primary-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Flame className="text-black" size={28} />
              </div>
              <span className="text-4xl font-bold text-gray-900 dark:text-white">
                {streak?.current_streak || 0}
              </span>
            </div>
            <div className="text-base font-semibold text-gray-900 dark:text-white">Day Streak</div>
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Longest: {streak?.longest_streak || 0} days
            </div>
          </div>
          
          {/* Lessons Completed */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-800 hover:border-primary-500 transition-all duration-200 group">
            <div className="flex items-center justify-between mb-3">
              <div className="w-14 h-14 bg-primary-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <CheckCircle className="text-black" size={28} />
              </div>
              <span className="text-4xl font-bold text-gray-900 dark:text-white">
                {stats?.totalLessonsCompleted || 0}
              </span>
            </div>
            <div className="text-base font-semibold text-gray-900 dark:text-white">Lessons Completed</div>
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {stats?.totalLessonsStarted || 0} started
            </div>
          </div>
          
          {/* Time Spent */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-800 hover:border-primary-500 transition-all duration-200 group">
            <div className="flex items-center justify-between mb-3">
              <div className="w-14 h-14 bg-primary-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Clock className="text-black" size={28} />
              </div>
              <span className="text-4xl font-bold text-gray-900 dark:text-white">
                {Math.floor((stats?.totalTimeMinutes || 0) / 60)}h
              </span>
            </div>
            <div className="text-base font-semibold text-gray-900 dark:text-white">Time Spent</div>
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {(stats?.totalTimeMinutes || 0) % 60}m
            </div>
          </div>
          
          {/* Courses */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-800 hover:border-primary-500 transition-all duration-200 group">
            <div className="flex items-center justify-between mb-3">
              <div className="w-14 h-14 bg-primary-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <BookOpen className="text-black" size={28} />
              </div>
              <span className="text-4xl font-bold text-gray-900 dark:text-white">
                {stats?.coursesInProgress || 0}
              </span>
            </div>
            <div className="text-base font-semibold text-gray-900 dark:text-white">Courses In Progress</div>
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Keep learning!
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Continue Learning */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl p-8 border border-gray-200 dark:border-gray-800 shadow-lg">
              <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">Continue Learning</h2>
              
              {progress?.courseStats && progress.courseStats.length > 0 ? (
                <div className="space-y-4">
                  {progress.courseStats.map((courseStat) => {
                    const course = courses.find(c => c.course_id === courseStat.courseId);
                    if (!course) return null;
                    
                    return (
                      <div key={courseStat.courseId} className="border border-gray-200 dark:border-gray-800 rounded-lg p-4 hover:border-primary-500 transition-colors">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-semibold text-lg text-gray-900 dark:text-white">{course.title}</h3>
                          <span className="text-sm font-medium text-primary-500">
                            {courseStat.percentage}%
                          </span>
                        </div>
                        
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-3">
                          <div
                            className="bg-primary-500 h-2 rounded-full transition-all"
                            style={{ width: `${courseStat.percentage}%` }}
                          ></div>
                        </div>
                        
                        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                          <span>{courseStat.completed} / {courseStat.totalLessons} lessons</span>
                          <Link
                            to={`/courses/${courseStat.courseId}`}
                            className="text-primary-500 hover:text-primary-600 font-medium inline-flex items-center"
                          >
                            Continue
                            <ArrowRight size={16} className="ml-1" />
                          </Link>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <BookOpen size={48} className="mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 mb-4">You haven't started any courses yet</p>
                  <Link to="/courses" className="btn-primary">
                    Browse Courses
                  </Link>
                </div>
              )}
            </div>
            
            {/* Available Courses */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl p-8 border border-gray-200 dark:border-gray-800 shadow-lg">
              <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">Available Courses</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {courses.map((course) => (
                  <Link
                    key={course.course_id}
                    to={`/courses/${course.course_id}`}
                    className="border border-gray-200 rounded-lg p-4 hover:border-primary-500 hover:shadow-md transition-all"
                  >
                    <h3 className="font-semibold mb-2">{course.title}</h3>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {course.description}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>{course.total_lessons} lessons</span>
                      <span>{course.duration_hours}h</span>
                      <span className="capitalize">{course.difficulty_level}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
          
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-900 rounded-2xl p-8 border border-gray-200 dark:border-gray-800 shadow-lg sticky top-4">
              <h3 className="font-bold text-2xl mb-6 text-gray-900 dark:text-white">Learning Stats</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Total Learning Days</span>
                  <span className="font-semibold">{streak?.total_learning_days || 0}</span>
                </div>
                
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Lessons Started</span>
                  <span className="font-semibold">{stats?.totalLessonsStarted || 0}</span>
                </div>
                
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Lessons Completed</span>
                  <span className="font-semibold">{stats?.totalLessonsCompleted || 0}</span>
                </div>
                
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-gray-600">Completion Rate</span>
                  <span className="font-semibold">
                    {stats?.totalLessonsStarted > 0
                      ? Math.round((stats.totalLessonsCompleted / stats.totalLessonsStarted) * 100)
                      : 0}%
                  </span>
                </div>
              </div>
              
              <div className="mt-6 p-6 bg-black dark:bg-gray-950 rounded-xl border border-primary-500">
                <div className="flex items-center gap-2 mb-3">
                  <Trophy className="text-primary-500" size={24} />
                  <span className="font-bold text-lg text-white">Keep Going!</span>
                </div>
                <p className="text-sm text-gray-300 leading-relaxed">
                  You're making great progress. Complete a lesson today to maintain your streak!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

